<template>
  <section class="demo sec" :style="{ '--accent': accent, '--accent2': accent2 }">
    <div class="demo-bg">
      <img :src="bgSrc" alt=""/>
      <span class="demo-mask"></span>
    </div>

    <div class="page demo-inner">
      <div class="demo-text">
        <span class="eyebrow reveal" v-reveal>{{ demo.label }}</span>
        <h2 class="h2 demo-title reveal" v-reveal="{ delay: 80 }">{{ demo.title }}</h2>
        <p class="lead demo-desc reveal" v-reveal="{ delay: 140 }">{{ demo.desc }}</p>

        <ul class="demo-points reveal" v-reveal="{ delay: 200 }">
          <li v-for="(p, i) in demo.points" :key="i">
            <IconSvg name="check"/>
            <span>{{ p }}</span>
          </li>
        </ul>

        <div class="demo-actions reveal" v-reveal="{ delay: 260 }">
          <a
            class="btn btn-primary"
            :href="demo.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconSvg name="play"/>
            进入在线演示
          </a>
          <button class="btn btn-ghost" type="button" @click="copyUrl">
            <IconSvg :name="copied ? 'check' : 'link'"/>
            {{ copied ? '演示地址已复制' : '复制演示地址' }}
          </button>
        </div>
      </div>

      <aside class="demo-card card reveal" v-reveal="{ delay: 160 }">
        <div class="demo-card-head">
          <span class="demo-card-icon"><IconSvg name="key"/></span>
          <div>
            <h3>{{ demo.credentialNote }}</h3>
            <p>{{ demo.contactHint }}</p>
          </div>
        </div>

        <dl class="demo-meta">
          <div>
            <dt>演示地址</dt>
            <dd><a :href="demo.url" target="_blank" rel="noopener noreferrer">{{ shownUrl }}</a></dd>
          </div>
          <div>
            <dt>联系电话</dt>
            <dd><a :href="`tel:${company.phone}`">{{ company.phone }}</a></dd>
          </div>
          <div>
            <dt>邮箱</dt>
            <dd><a :href="`mailto:${company.email}`">{{ company.email }}</a></dd>
          </div>
        </dl>

        <router-link to="/contact" class="btn btn-primary demo-card-btn">
          联系我们获取账号
          <IconSvg name="arrow-right"/>
        </router-link>

        <p class="demo-note">
          演示环境为公开测试数据，请勿录入真实生产信息；如需 POC 环境或定向演示，我们可单独开通。
        </p>
      </aside>
    </div>
  </section>
</template>

<script>
import IconSvg from './IconSvg'
import img from '@/utils/assets'
import { company, demo } from '@/data/site'

export default {
  name: 'DemoBand',
  components: { IconSvg },
  props: {
    bg: { type: String, default: 'gen/demo-preview.jpg' },
    accent: { type: String, default: '#22e0ff' },
    accent2: { type: String, default: '#3d7bff' }
  },
  data() {
    return { company, demo, img, copied: false }
  },
  computed: {
    bgSrc() {
      return img(this.bg)
    },
    shownUrl() {
      return this.demo.url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    }
  },
  methods: {
    async copyUrl() {
      const text = this.demo.url
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copied = true
        setTimeout(() => { this.copied = false }, 2200)
      } catch (e) {
        this.copied = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.demo {
  position: relative;
  overflow: hidden;
}

.demo-bg {
  position: absolute;
  inset: 0;

  img { width: 100%; height: 100%; object-fit: cover; opacity: 0.34; }

  .demo-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 55% at 78% 30%, color-mix(in srgb, var(--accent) 26%, transparent), transparent 70%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.9), rgba(4, 9, 22, 0.94));
  }
}

.demo-inner {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 46px;
  align-items: center;
}

.demo-title { margin-top: 16px; }
.demo-desc { margin-top: 18px; max-width: 640px; }

.demo-points {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 26px;

  li {
    display: flex;
    align-items: center;
    gap: 11px;
    font-size: 14.5px;
    color: var(--txt-2);
  }

  svg { width: 17px; height: 17px; color: var(--accent-txt); flex-shrink: 0; }
}

.demo-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 34px;
}

.demo-card {
  padding: 30px 28px 26px;
  border-radius: 20px;
}

.demo-card-head {
  display: flex;
  gap: 14px;
  align-items: flex-start;

  h3 { font-size: 17.5px; color: #fff; }
  p { margin-top: 9px; font-size: 13.5px; line-height: 1.8; color: var(--txt-3); }
}

.demo-card-icon {
  width: 44px;
  height: 44px;
  padding: 11px;
  flex-shrink: 0;
  border-radius: 13px;
  color: var(--accent-txt);
  border: 1px solid color-mix(in srgb, var(--accent) 45%, transparent);
  background: color-mix(in srgb, var(--accent) 12%, transparent);
}

.demo-meta {
  margin: 24px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 0 0;
  border-top: 1px solid rgba(120, 176, 255, 0.12);

  div { display: flex; gap: 14px; align-items: baseline; }
  dt { flex: 0 0 62px; font-size: 12.5px; color: var(--txt-3); }
  dd { margin: 0; font-size: 14px; color: var(--txt); word-break: break-all; }
  /* 链接本身是触点：给足纵向高度（WCAG 2.5.8 目标尺寸，移动端 ≥ 24px，实测 37px） */
  a {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    padding: 2px 0;
    border-bottom: 1px solid transparent;
    transition: border-color 0.3s, color 0.3s;
  }
  a:hover { color: var(--accent-txt); border-color: var(--accent); }
}

.demo-card-btn {
  width: 100%;
  margin-top: 26px;
}

.demo-note {
  margin-top: 18px;
  font-size: 12.5px;
  line-height: 1.75;
  color: var(--txt-3);
}

@media (max-width: 1080px) {
  .demo-inner { grid-template-columns: minmax(0, 1fr); gap: 32px; }
}

@media (max-width: 760px) {
  .demo-meta div { flex-direction: column; gap: 4px; }
  .demo-meta dt { flex: none; }
}
</style>
